import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Avatar } from "@/components/Avatar";
import { api, errorMessage } from "@/lib/api";
import { uploadImage } from "@/lib/upload";

export function ProfileEditor({ open, profile, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", avatar_url: "", private_hearts: false });
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (open) setForm({ name: "", avatar_url: "", private_hearts: false, ...(profile || {}) }); }, [open, profile]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { setForm((f) => ({ ...f, avatar_url: "" })); const url = await uploadImage(api, file); setForm((f) => ({ ...f, avatar_url: url })); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onSaved(); onClose(); } catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const body = { name: form.name, avatar_url: form.avatar_url, private_hearts: form.private_hearts };
  const save = () => run(() => (profile ? api.patch(`/family/profiles/${profile.id}`, body) : api.post("/family/profiles", body)), profile ? "Profile updated" : "Profile added");
  const remove = () => run(() => api.delete(`/family/profiles/${profile.id}`), "Profile removed");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="profile-editor" className="bg-[#151C2C] border-[#26334D] text-white max-w-sm">
        <DialogHeader>
          <DialogTitle>{profile ? "Edit profile" : "Add a family member"}</DialogTitle>
          <DialogDescription className="text-slate-400">Each member gets their own name and photo on reviews.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-4">
          <button type="button" data-testid="profile-editor-photo" onClick={() => ref.current?.click()} disabled={busy} className="relative rounded-3xl overflow-hidden">
            <Avatar url={form.avatar_url} name={form.name || "?"} size="h-20 w-20" text="text-3xl" className="!rounded-3xl" />
            <span className="absolute inset-0 bg-[#0B0F19]/50 grid place-items-center">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}</span>
          </button>
          <input ref={ref} type="file" accept="image/*" hidden data-testid="profile-editor-photo-input" onChange={pick} />
          <Input data-testid="profile-editor-name" value={form.name} maxLength={30} placeholder="Name" onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="h-12 rounded-xl bg-[#0B0F19] border-[#26334D]" />
        </div>
        <label className="flex items-center justify-between gap-3 text-sm text-slate-300">
          Keep my hearts private from the family
          <Switch data-testid="profile-editor-private" checked={form.private_hearts} onCheckedChange={(v) => setForm({ ...form, private_hearts: v })} />
        </label>
        <div className="flex gap-2">
          {profile && <Button data-testid="profile-editor-delete" variant="outline" onClick={remove} disabled={busy} className="rounded-full border-[#FF3B6B]/50 text-[#FF7A7A] bg-transparent hover:bg-[#FF3B6B]/10"><Trash2 className="h-4 w-4" /></Button>}
          <Button data-testid="profile-editor-save" onClick={save} disabled={busy || !form.name.trim()} className="flex-1 rounded-full bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold">{profile ? "Save" : "Add profile"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
